import { useQuery } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getDistrictsWithRegionsTree } from '@/data/territoryRepository';
import type { Scope } from '../types';
import type { ScopeValue } from '../utils/scope';

const SCOPES: { value: Scope; label: string; hint: string }[] = [
  { value: 'NATIONAL', label: 'Nacional', hint: 'Todo el país' },
  { value: 'DISTRICT', label: 'Distrito', hint: 'Un distrito y sus regiones' },
  { value: 'REGION', label: 'Región', hint: 'Una región' },
];

interface ScopeFieldsProps {
  value: ScopeValue;
  onChange: (value: ScopeValue) => void;
  idPrefix: string;
}

/** Scope of a role assignment: nationwide, one district or one region */
export function ScopeFields({ value, onChange, idPrefix }: ScopeFieldsProps) {
  const { data: districts = [] } = useQuery({
    queryKey: ['territory-tree'],
    queryFn: getDistrictsWithRegionsTree,
    staleTime: 10 * 60 * 1000,
  });
  const selectedDistrict = districts.find((d) => d.id === value.districtId);

  return (
    <div className="space-y-3">
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Alcance</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {SCOPES.map((s) => (
            <label
              key={s.value}
              className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${
                value.scope === s.value ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted'
              }`}
              title={s.hint}
            >
              <input
                type="radio"
                name={`${idPrefix}-scope`}
                className="sr-only"
                checked={value.scope === s.value}
                onChange={() => onChange({ scope: s.value, districtId: '', regionId: '' })}
              />
              {s.label}
            </label>
          ))}
        </div>
      </fieldset>

      {value.scope !== 'NATIONAL' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-district`}>Distrito</Label>
          <Select
            value={value.districtId}
            onValueChange={(districtId) => onChange({ ...value, districtId, regionId: '' })}
          >
            <SelectTrigger id={`${idPrefix}-district`}><SelectValue placeholder="Selecciona un distrito" /></SelectTrigger>
            <SelectContent>
              {districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {value.scope === 'REGION' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-region`}>Región</Label>
          <Select
            value={value.regionId}
            disabled={!selectedDistrict}
            onValueChange={(regionId) => onChange({ ...value, regionId })}
          >
            <SelectTrigger id={`${idPrefix}-region`}><SelectValue placeholder="Selecciona una región" /></SelectTrigger>
            <SelectContent>
              {(selectedDistrict?.regions ?? []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
